import { Download } from "@mui/icons-material";
import { IconButton, IconButtonProps, Tooltip } from "@mui/material";
import TransitionalRegion from "components/TransitionalRegion";
import VIDEO_TYPE from "enums/VIDEO_TYPE";
import useGetParticipantById from "hooks/api/useGetParticipantById";
import useGetSignedVideoUrls from "hooks/api/useGetSignedVideoUrls";
import useGetVideoById from "hooks/api/useGetVideoById";
import useCreateDownload from "hooks/useCreateDownload";
import { useContext } from "react";
import getVideoDownloadFilename from "utils/getVideoDownloadFilename";
import TimelinePlayerContext from "../context/TimelinePlayerContext";

interface Props extends IconButtonProps {
  videoId?: string;
}

export default function DownloadVideoButton({ videoId, ...rest }: Props) {
  const { currentTimelineItem, videoId: contextVideoId } = useContext(
    TimelinePlayerContext,
  );
  const effectiveVideoId = videoId ?? contextVideoId;
  const { video } = useGetVideoById(effectiveVideoId, {
    suspendRequest: Boolean(currentTimelineItem?.signedSourceUrl),
  });
  const { video: parentVideo } = useGetVideoById(video?.parentId, {
    suspendRequest: !video?.parentId,
  });
  const { participant } = useGetParticipantById(
    video?.participantId || parentVideo?.participantId,
  );
  const { transcodeUrl } = useGetSignedVideoUrls(effectiveVideoId, {
    suspendRequest: Boolean(currentTimelineItem?.signedSourceUrl),
  });
  const downloadUrl = currentTimelineItem?.signedSourceUrl || transcodeUrl;
  const filename = getVideoDownloadFilename(video, participant);
  const download = useCreateDownload(downloadUrl, filename);

  if (
    download.isDownloading ||
    download.downloadProgress == 100 ||
    currentTimelineItem?.allowDownload == false ||
    (currentTimelineItem?.type != VIDEO_TYPE.VIDEO &&
      currentTimelineItem?.type != VIDEO_TYPE.CLIP &&
      currentTimelineItem?.type != VIDEO_TYPE.INSIGHT) ||
    rest.disabled
  )
    return;

  return (
    <IconButton
      title={
        download.error
          ? `Unable to download ${video?.title}`
          : `Download ${video?.title}`
      }
      size="large"
      sx={{
        padding: 0,
        color: (theme) => theme.palette.secondary.dark,
      }}
      onClick={download.onDownload}
    >
      {download.error ? (
        <Download />
      ) : (
        <TransitionalRegion
          showSpinner={download.isDownloading}
          percentComplete={download.downloadProgress}
        >
          <Tooltip title="Download Video">
            <Download />
          </Tooltip>
        </TransitionalRegion>
      )}
    </IconButton>
  );
}
